import { useQuery } from "@tanstack/react-query";
import { Check, CirclePlay } from "lucide-react";
import { RoutinesIcon } from "@/shared/icons";
import { companyStatsQueryOptions } from "./queries";

interface TileProps {
  value: number | undefined;
  label: string;
  variant: "ok" | "warn" | "active" | "muted";
  icon: React.ReactNode;
}

function Tile({ value, label, variant, icon }: TileProps) {
  return (
    <div className={`company-kpi-tile company-kpi-tile--${variant}`}>
      <span className="company-kpi-icon" aria-hidden>
        {icon}
      </span>
      <div className="company-kpi-body">
        <div className="company-kpi-value">{value ?? "—"}</div>
        <div className="company-kpi-label">{label}</div>
      </div>
    </div>
  );
}

// Match the glyphs used by SessionTypeIcon (sidebar history) so the KPI
// tiles read as the same vocabulary as the row badges below them.
const CheckGlyph = <Check size={20} strokeWidth={2.25} aria-hidden />;
// Same exclamation as SessionTypeIcon's `badge` variant for awaiting —
// kept inline because that component doesn't export it.
const WarnGlyph = (
  <svg viewBox="0 0 32 32" width="20" height="20" fill="none" aria-hidden>
    <rect x="14.4" y="8" width="3.2" height="12" rx="1.6" fill="currentColor" />
    <circle cx="16" cy="23.2" r="1.6" fill="currentColor" />
  </svg>
);
const PlayGlyph = <CirclePlay size={20} strokeWidth={1.75} aria-hidden />;
// Same icon as the «Автоматизации» entry in the sidebar.
const LoopGlyph = <RoutinesIcon width={20} height={20} aria-hidden />;

export function CompanyKPIs() {
  const { data } = useQuery(companyStatsQueryOptions());

  return (
    <section className="company-kpis">
      <Tile
        value={data?.completed_7d}
        label="Задач завершено за 7 дней"
        variant="ok"
        icon={CheckGlyph}
      />
      <Tile
        value={data?.awaiting_user}
        label="Требуют внимания"
        variant="warn"
        icon={WarnGlyph}
      />
      <Tile
        value={data?.in_progress}
        label="Сейчас в работе"
        variant="active"
        icon={PlayGlyph}
      />
      <Tile
        value={data?.routines_enabled}
        label="Активных автоматизаций"
        variant="muted"
        icon={LoopGlyph}
      />
    </section>
  );
}
